import { useTranslation } from "react-i18next";
import { AudioWaveform } from "@/components/ui/audio-waveform";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import {
  MAYA_ACCEPT_ATTRIBUTE,
  useMayaFileUploads,
} from "@/hooks/useMayaFileUploads";
import { AttachmentChips } from "@/components/ai-assistant/attachment-chips";
import {
  MayaForm,
  MAYA_FORM_MAX_MSG,
  type MayaFormSpec,
} from "@/components/ai-assistant/maya-form";
import type {
  IChatAttachment,
  TPromptInputRef,
} from "@/types/(my-space)/ai-assistant.interface";
import { cn } from "@/utils/cn";
import { AnimatePresence, motion } from "framer-motion";
import type { FormikProps } from "formik";
import { ArrowUp, Check, Mic, Paperclip, Square, X } from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import toast from "react-hot-toast";
import {
  AI_PROMPT_MAX_LENGTH,
  aiPromptFieldName,
  type ChatBotFields,
} from "./context";

// The field grows with its content up to 6 lines, then scrolls. Keep in sync
// with the textarea's leading-6 (24px) and py-3 (12px top + bottom).
const PROMPT_LINE_HEIGHT_PX = 24;
const PROMPT_PADDING_Y_PX = 24;
const PROMPT_MAX_ROWS = 6;
const PROMPT_MAX_HEIGHT_PX =
  PROMPT_LINE_HEIGHT_PX * PROMPT_MAX_ROWS + PROMPT_PADDING_Y_PX;

const autoResize = (el: HTMLTextAreaElement) => {
  el.style.height = "auto";
  el.style.height = `${Math.min(el.scrollHeight, PROMPT_MAX_HEIGHT_PX)}px`;
  el.style.overflowY =
    el.scrollHeight > PROMPT_MAX_HEIGHT_PX ? "auto" : "hidden";
};

interface PromptInputProps {
  formik: FormikProps<ChatBotFields>;
  isLoading: boolean;
  isTempChat: boolean;
  promptInputRef: TPromptInputRef;
  layoutId: string;
  transcribe: (audioBlob: Blob) => Promise<string>;
  onSend?: (text: string, attachments: IChatAttachment[]) => unknown;
  uploadEndpoint?: string;
  /** Maya interactive form to render above the composer. Null = hidden. */
  activeForm?: MayaFormSpec | null;
  onCloseForm?: () => void;
  /** Stop the in-flight generation. When set + isLoading, send becomes stop. */
  onStop?: () => void;
  /** Surfaces this composer's addFiles upward so a page-level drop zone can
   *  funnel files into the same upload state (chips render here). */
  exposeAddFiles?: (addFiles: (files: FileList | File[]) => void) => void;
  /** Icon-sized control rendered just before the mic (Advisor picker). Hidden
   *  while recording, where the row belongs to the waveform. */
  actionSlot?: ReactNode;
  /** Overrides the idle placeholder (e.g. per selected advisor). */
  placeholder?: string;
}

export const PromptInput = ({
  formik,
  isLoading,
  isTempChat,
  promptInputRef,
  layoutId,
  transcribe,
  onSend,
  uploadEndpoint,
  activeForm,
  onCloseForm,
  onStop,
  exposeAddFiles,
  actionSlot,
  placeholder,
}: PromptInputProps) => {
  const { t } = useTranslation("my-space");
  const idlePlaceholder = placeholder ?? t("ui.ask_me_anything");
  const recorder = useVoiceRecorder();
  const [isTranscribing, setIsTranscribing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const internalInputRef = useRef<HTMLTextAreaElement>(null);

  const setInputRef = (el: HTMLTextAreaElement | null) => {
    internalInputRef.current = el;
    if (promptInputRef && "current" in promptInputRef) {
      (promptInputRef as React.RefObject<HTMLTextAreaElement | null>).current =
        el;
    }
  };

  // Re-fit on every value change (typing, voice merge, Maya form, reset) and
  // when the field remounts after recording.
  const promptValue = formik.values[aiPromptFieldName] ?? "";
  useLayoutEffect(() => {
    if (internalInputRef.current) autoResize(internalInputRef.current);
  }, [promptValue, recorder.isRecording]);

  // Keep focus on the field even while a response streams — the user can type
  // their next message ahead (send is gated until generation finishes).
  useEffect(() => {
    if (isTranscribing || recorder.isRecording) return;
    const t = window.setTimeout(() => {
      internalInputRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(t);
  }, [isTranscribing, recorder.isRecording]);

  const {
    attachments,
    addFiles,
    removeAttachment,
    retryAttachment,
    clear: clearAttachments,
    hasUploading,
    successAttachments,
  } = useMayaFileUploads(uploadEndpoint);

  const finalizeRecording = async () => {
    let text = "";
    try {
      const audioBlob = await recorder.stop();
      setIsTranscribing(true);
      text = await transcribe(audioBlob);
    } catch (err) {
      console.error(err);
      toast.error(t("toasts.could_not_transcribe_audio"));
      setIsTranscribing(false);
      return;
    }
    setIsTranscribing(false);
    const current = formik.values[aiPromptFieldName] ?? "";
    const merged = current ? `${current} ${text}`.trim() : text.trim();
    if (!merged) return;
    formik.setFieldValue(aiPromptFieldName, merged);
    internalInputRef.current?.focus();
  };

  const handleMicClick = async () => {
    if (isLoading || isTranscribing || recorder.isRecording) return;
    try {
      await recorder.start({
        onTimeout: () =>
          toast.error(t("toasts.recording_stopped_2_minute_limit_reached")),
      });
    } catch {
      const err = recorder.error;
      if (err === "permission-denied")
        toast.error(t("toasts.microphone_permission_denied"));
      else if (err === "no-device")
        toast.error(t("toasts.no_microphone_found"));
      else if (err === "unsupported")
        toast.error(t("toasts.voice_input_not_supported_in_this_browser"));
      else toast.error(t("toasts.could_not_start_recording"));
    }
  };

  const handleCancelRecording = () => {
    recorder.cancel();
  };

  const handlePaperclipClick = () => {
    if (isTranscribing) return;
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    addFiles(files);
    e.target.value = "";
  };

  // Surface addFiles so a page-level FileDropZone can funnel files dropped
  // anywhere in the chat area into this composer's upload state (chips here).
  useEffect(() => {
    exposeAddFiles?.(addFiles);
  }, [exposeAddFiles, addFiles]);

  // Paste files (e.g. a screenshot) straight into the prompt field. addFiles
  // reuses the same type/size/count validation as the picker. Text paste falls
  // through to the default handler.
  const handlePaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData?.files ?? []);
    if (files.length === 0) return;
    e.preventDefault();
    addFiles(files);
  };

  const dispatchSend = async () => {
    // Enter is ignored while streaming — the field stays open for type-ahead,
    // but sending waits until the current response finishes (avoids clearing
    // the draft/attachments before the parent's isLoading guard rejects it).
    if (isLoading) return;
    const text = (formik.values[aiPromptFieldName] ?? "").trim();
    if (!text && successAttachments.length === 0) return;
    // maxLength only stops typing/pasting; a voice merge can still overshoot.
    if (text.length > AI_PROMPT_MAX_LENGTH) {
      toast.error(
        t("toasts.message_is_too_long_max_characters", {
          count: AI_PROMPT_MAX_LENGTH,
        }),
      );
      return;
    }
    if (hasUploading) {
      toast.error(t("toasts.wait_for_attachments_to_finish_uploading"));
      return;
    }
    if (onSend) {
      formik.resetForm();
      clearAttachments();
      await Promise.resolve(onSend(text, successAttachments));
    } else {
      formik.handleSubmit();
      clearAttachments();
    }
  };

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (recorder.isRecording) {
      await finalizeRecording();
      return;
    }
    await dispatchSend();
  };

  // Enter sends, Shift+Enter inserts a newline. Skip while an IME is composing
  // so confirming a candidate doesn't send.
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    e.currentTarget.form?.requestSubmit();
  };

  // Maya form submit composes "Label: value" lines INTO the composer for the
  // user to review/edit/attach/send — it never auto-sends (docs §A.6 invariant).
  const handleMayaFormSubmit = (text: string, trimmed: boolean) => {
    const current = (formik.values[aiPromptFieldName] ?? "").trim();
    let merged = current ? `${current}\n${text}` : text;
    let didTrim = trimmed;
    if (merged.length > MAYA_FORM_MAX_MSG) {
      merged = merged.slice(0, MAYA_FORM_MAX_MSG);
      didTrim = true;
    }
    formik.setFieldValue(aiPromptFieldName, merged);
    onCloseForm?.();
    requestAnimationFrame(() => {
      const el = internalInputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
        el.scrollTop = el.scrollHeight;
      }
    });
    toast.success(t("toasts.added_to_your_message_edit_attach_then_send"));
    if (didTrim) toast(t("toasts.trimmed_to_fit_the_message_limit"));
  };

  const hasPromptText = !!formik.values[aiPromptFieldName]?.trim();
  const hasReadyAttachments = successAttachments.length > 0;
  // While streaming the send button becomes a Stop button; sendDisabled only
  // governs the Send state.
  const sendDisabled =
    isTranscribing ||
    hasUploading ||
    (!recorder.isRecording && !hasPromptText && !hasReadyAttachments);
  const showStop = isLoading && !recorder.isRecording && !!onStop;

  return (
    // data-composer-*: the parts Maya's launcher works with on the AI
    // Assistant page — it keeps clear of the area and glows the frame.
    <div data-composer-area className="relative w-full">
      {/* Form card floats above the composer as a sibling — never nested inside
          the composer <form>, which would be invalid HTML. */}
      <AnimatePresence>
        {activeForm && (
          <motion.div
            key="maya-form"
            initial={{ opacity: 0, y: 28, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{
              type: "spring",
              stiffness: 460,
              damping: 30,
              mass: 0.7,
            }}
            className="absolute bottom-full start-0 end-0 z-30 mb-2 origin-bottom"
          >
            <MayaForm
              spec={activeForm}
              onSubmit={handleMayaFormSubmit}
              onClose={() => onCloseForm?.()}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <motion.form
        className="w-full"
        onSubmit={handleFormSubmit}
        layoutId={layoutId}
        transition={{ type: "spring", stiffness: 600, damping: 45, mass: 0.6 }}
      >
        <div
          data-composer-frame
          className={isTempChat ? "gradient-border-dashed" : "gradient-border"}
          style={
            isLoading
              ? { background: "var(--color-surface-elevated)" }
              : undefined
          }
        >
          <div className="bg-white rounded-sm overflow-hidden flex flex-col">
            {attachments.length > 0 && (
              <AttachmentChips
                attachments={attachments}
                onRemove={removeAttachment}
                onRetry={retryAttachment}
                className="px-3 pt-2"
              />
            )}
            <div className="flex items-end gap-2 ps-2 pe-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={MAYA_ACCEPT_ATTRIBUTE}
                onChange={handleFileInputChange}
                className="hidden"
              />
              {!recorder.isRecording && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={handlePaperclipClick}
                        disabled={isTranscribing}
                        aria-label={t("labels.attach_files")}
                        className={cn(
                          "size-9 shrink-0 mb-1.5 rounded flex items-center justify-center transition-colors",
                          "disabled:opacity-50 disabled:cursor-not-allowed",
                          "text-slate-600 hover:bg-slate-100",
                        )}
                      >
                        <Paperclip className="size-5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      {t("my-space:ui.attach_files_images_and_pdf")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {recorder.isRecording ? (
                <span
                  className="flex-1 flex items-center gap-2 px-5 h-12 text-xs font-medium text-slate-500"
                  aria-live="polite"
                >
                  <span className="size-2 rounded-full bg-red-500 animate-mic-pulse" />
                  <AudioWaveform
                    stream={recorder.stream}
                    active={recorder.isRecording}
                    barCount={20}
                    className="h-5"
                  />
                  {t("common:ai_assistant.listening_tap_send")}
                </span>
              ) : (
                <textarea
                  ref={setInputRef}
                  autoFocus
                  rows={1}
                  autoComplete="off"
                  autoCapitalize="sentences"
                  maxLength={AI_PROMPT_MAX_LENGTH}
                  placeholder={
                    isTranscribing ? t("ui.transcribing") : idlePlaceholder
                  }
                  disabled={isTranscribing}
                  onPaste={handlePaste}
                  onKeyDown={handleKeyDown}
                  className={cn(
                    "flex-1 min-w-0 resize-none overflow-hidden bg-transparent py-3 ps-1 pe-5 text-base leading-6 font-medium text-foreground outline-hidden",
                    "placeholder:text-black/50 selection:bg-primary selection:text-white",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                  style={{ maxHeight: PROMPT_MAX_HEIGHT_PX }}
                  {...formik.getFieldProps(aiPromptFieldName)}
                />
              )}
              <div className="flex items-center gap-2 shrink-0 h-12">
                {!recorder.isRecording && actionSlot}
                {recorder.isRecording ? (
                  <button
                    type="button"
                    onClick={handleCancelRecording}
                    aria-label={t("labels.cancel_recording")}
                    className="size-9 rounded-md flex items-center justify-center text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                  >
                    <X className="size-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleMicClick}
                    disabled={isLoading || isTranscribing}
                    aria-label={t("labels.start_recording")}
                    className={cn(
                      "size-9 rounded flex items-center justify-center transition-colors",
                      "disabled:opacity-50 disabled:cursor-not-allowed",
                      "text-slate-600 hover:bg-slate-100",
                    )}
                  >
                    {isTranscribing ? (
                      <Spinner size="sm" className="text-slate-500" />
                    ) : (
                      <Mic className="size-5" />
                    )}
                  </button>
                )}

                {showStop ? (
                  <button
                    type="button"
                    onClick={onStop}
                    aria-label={t("labels.stop_generating")}
                    className={cn(
                      "size-9 rounded flex items-center justify-center text-sm font-medium text-white shadow-xs transition-opacity",
                      "bg-[linear-gradient(135deg,var(--color-primary)_0%,var(--color-info)_100%)]",
                      "hover:opacity-90 active:scale-95",
                    )}
                  >
                    <Square className="size-3.5 fill-current" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={sendDisabled || isLoading}
                    aria-label={
                      recorder.isRecording
                        ? t("ui.finish_recording")
                        : t("ui.send")
                    }
                    className={cn(
                      "size-9 rounded flex items-center justify-center text-sm font-medium text-white shadow-xs transition-opacity",
                      "bg-[linear-gradient(135deg,var(--color-primary)_0%,var(--color-info)_100%)]",
                      "disabled:opacity-40 disabled:cursor-not-allowed",
                    )}
                  >
                    {isLoading ? (
                      <Spinner
                        size="sm"
                        className="text-white"
                        bgClassName="stroke-white/30"
                      />
                    ) : recorder.isRecording ? (
                      <Check className="size-5" />
                    ) : (
                      <ArrowUp className="size-5" />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.form>
    </div>
  );
};
